import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef, MatDialogModule } from '@angular/material/dialog';
import { MatAutocompleteSelectedEvent, MatAutocompleteModule } from '@angular/material/autocomplete';
import { DatePipe } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { EquipmentManagenementService } from '../../../../services/equipmentmanagement.service';
import { ToastrService } from 'ngx-toastr';
import { TextAreaDialogComponent } from '../../../utils/textareadialog/textarea-dialog.component';
import { Utils } from '../../../../utils/utils';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatMenuModule } from '@angular/material/menu';
import { MatCardModule } from '@angular/material/card';
import { NgClass } from '@angular/common';

@Component({
    selector: 'modelfiledialog',
    standalone: true,
    imports: [
        MatDialogModule,
        MatFormFieldModule,
        MatInputModule,
        MatButtonModule,
        MatIconModule,
        MatSelectModule,
        MatCheckboxModule,
        MatTooltipModule,
        MatAutocompleteModule,
        MatToolbarModule,
        MatMenuModule,
        MatCardModule,
        ReactiveFormsModule,
        FormsModule,
        NgClass
    ],
    providers: [DatePipe],
    templateUrl: './modelfiledialog.component.html',
    styleUrls: ['./modelfiledialog.component.less']
})
export class ModelFileDialogComponent implements OnInit {
    editForm!: FormGroup;
    filteredLanguages: Language[] = [];
    originalFormValue!: any;
    fileContent: string = '';
    isFileChanged: boolean = false;
    availableModelFiles: EMTModelFile[] = [];
    filteredModelFiles: EMTModelFile[] = [];
    selectedModelFileId: number = -1;
    selectedModelFilePath: string = '';
    modelFilesLoaded: boolean = false;

    constructor(@Inject(MAT_DIALOG_DATA) public data: {
        modelFileForm: FormGroup,
        isNew: boolean,
        softwareReleaseId: number,
        familyCodeId: number,
        metadataLabel: string,
        modifUser?: string,
        comment?: string,
        releaseDate?: string,
        releaseData?: any
    },
        public datePipe: DatePipe
        , private readonly emtService: EquipmentManagenementService
        , private readonly fb: FormBuilder
        , private readonly dialogRef: MatDialogRef<ModelFileDialogComponent>
        , private readonly http: HttpClient
        , @Inject('BASE_URL') private readonly baseUrl: string
        , private readonly toastrService: ToastrService
        , private readonly dialogForm: MatDialog) { }

    ngOnInit(): void {
        if (this.data.isNew) {
            // New model file: create a new form group with default values
            this.originalFormValue = {
                file: null,
                fileName: '',
                modelFileId: 0,
                releaseName: '',
                comment: ''
            };
            this.editForm = this.fb.group({
                ...this.originalFormValue,
                releaseName: ['', Validators.required],
            });
        } else {
            // Existing model file: clone the form value into a new FormGroup
            this.originalFormValue = this.data.modelFileForm.value;
            this.editForm = this.fb.group({
                ...this.originalFormValue,
            });

            if (this.editForm.value.modelFileId) {
                this.selectedModelFileId = this.editForm.value.modelFileId;
            }

            // Load existing file content if available
            if (this.editForm.value.modelFileId) {
                Utils.httpDownloadControllerSoftwareModelFile(
                    this.http,
                    this.baseUrl,
                    this.editForm.value.modelFileId,
                    this,
                    (tthis: ModelFileDialogComponent, result: any) => {
                        if (result && !result.error) {
                            tthis.fileContent = result;
                        }
                    }
                );
            }
        }
    }


    onFileChange(event: any) {
        const files = (event?.target as HTMLInputElement)?.files;
        if (files?.length) {
            const fileArray = Array.from(files);
            this.editForm.get('file')?.setValue(fileArray);

            const fileNames = fileArray.map(f => f.name).join(', ');
            this.editForm.get('fileName')?.setValue(fileNames);
            this.isFileChanged = true;
            
            // Clear dropdown selection when uploading a new file
            this.selectedModelFileId = -1;
            this.selectedModelFilePath = '';
            this.editForm.patchValue({ modelFileId: 0 });
        } else {
            this.editForm.get('file')?.setValue(null);
            this.editForm.get('fileName')?.setValue('');
            this.editForm.get('releaseName')?.setValue('');
        }
    }

    diffValues(): boolean {
        if (this.data.isNew) {
            return true;
        }
        const current = this.editForm.value;
        const original = this.originalFormValue;

        return current.fileName !== original.fileName;
    }

    onEditFileContent() {
        const dialogRef = this.dialogForm.open(TextAreaDialogComponent, {
            height: '450px',
            width: '650px',
            data: {
                title: `Edit ${this.editForm.value.fileName}`,
                value: this.fileContent
            },
            panelClass: 'details-dialog-panel',
            disableClose: true,
        });

        dialogRef.afterClosed().subscribe(result => {
            if (result) {
                // For model files, we might need a different approach to save content
                // For now, update the local content
                this.fileContent = result;
                this.toastrService.success('File content updated locally. Save to persist changes.');
            }
        });
    }

    onSave() {
        // Case 0: Edit existing model file - update release name and/or comment
        if (!this.data.isNew) {
            const releaseName = this.editForm.value.releaseName?.trim();
            const comment = this.editForm.value.comment?.trim();
            
            if (!releaseName) {
                this.toastrService.error('Release Name is required');
                return;
            }

            const modelFileId = this.editForm.value.modelFileId;
            if (!modelFileId || modelFileId <= 0) {
                this.toastrService.error('Invalid model file ID');
                return;
            }

            // Detect what has changed
            const releaseNameChanged = releaseName !== this.originalFormValue.releaseName?.trim();
            const commentChanged = comment !== this.originalFormValue.comment?.trim();

            if (!releaseNameChanged && !commentChanged) {
                this.toastrService.info('No changes detected');
                return;
            }

            // Update release name first if changed
            if (releaseNameChanged) {
                Utils.httpUpdateModelFileReleaseName(
                    this.http,
                    this.baseUrl,
                    modelFileId,
                    releaseName,
                    this,
                    (tthis: ModelFileDialogComponent, result: any) => {
                        if (result && !result.error) {
                            // If comment also changed, update it
                            if (commentChanged) {
                                Utils.httpUpdateModelFileComment(
                                    tthis.http,
                                    tthis.baseUrl,
                                    modelFileId,
                                    comment,
                                    tthis,
                                    (tthis2: ModelFileDialogComponent, result2: any) => {
                                        if (result2 && !result2.error) {
                                            tthis2.dialogRef.close({
                                                success: true,
                                                modelFileId: modelFileId,
                                                releaseName: releaseName,
                                                comment: comment
                                            });
                                        } else {
                                            tthis2.toastrService.error(result2?.errorMssg || 'Failed to update comment');
                                        }
                                    }
                                );
                            } else {
                                // Only release name changed
                                tthis.dialogRef.close({
                                    success: true,
                                    modelFileId: modelFileId,
                                    releaseName: releaseName
                                });
                            }
                        } else {
                            tthis.toastrService.error(result?.errorMssg || 'Failed to update release name');
                        }
                    }
                );
            } else if (commentChanged) {
                // Only comment changed
                Utils.httpUpdateModelFileComment(
                    this.http,
                    this.baseUrl,
                    modelFileId,
                    comment,
                    this,
                    (tthis: ModelFileDialogComponent, result: any) => {
                        if (result && !result.error) {
                            tthis.dialogRef.close({
                                success: true,
                                modelFileId: modelFileId,
                                comment: comment
                            });
                        } else {
                            tthis.toastrService.error(result?.errorMssg || 'Failed to update comment');
                        }
                    }
                );
            }
            return;
        }

        // Case 1: Selected from dropdown (existing file) - Update in database
        if (this.selectedModelFileId > 0 && !this.editForm.value.file) {
            // Validate that we have the softwareReleaseId
            if (!this.data.softwareReleaseId || this.data.softwareReleaseId <= 0) {
                this.toastrService.error('Invalid software release ID');
                return;
            }

            // Call new endpoint to update ModelFileId in database
            Utils.httpUpdateControllerSoftwareModelFile(
                this.http,
                this.baseUrl,
                this.data.softwareReleaseId,
                this.selectedModelFileId,
                this,
                (tthis: ModelFileDialogComponent, result: any) => {
                    if (result && !result.error) {
                        // Get the release name from the selected model file
                        const selectedFile = tthis.availableModelFiles.find(f => f.modelFileId === tthis.selectedModelFileId);
                        tthis.dialogRef.close({
                            success: true,
                            errorMssg: null,
                            modelFileId: tthis.selectedModelFileId,
                            fileName: tthis.selectedModelFilePath,
                            releaseName: selectedFile?.releaseName || ''
                        });
                    } else {
                        tthis.dialogRef.close({
                            success: false,
                            errorMssg: result?.errorMssg
                        });
                    }
                }
            );
            return;
        }

        // Case 2: Upload new file
        let formData: FormData = new FormData();

        // Attach model file if present (new upload)
        if (this.editForm.value.file && this.editForm.value.fileName) {
            const file = this.editForm.value.file;
            if (Array.isArray(file)) {
                formData.append('file', file[0], this.editForm.value.fileName);
            } else {
                formData.append('file', file, this.editForm.value.fileName);
            }
        } else {
            // If no file selected from either source, show error
            this.toastrService.error('Please select or upload a file');
            return;
        }

        // Validate ReleaseName is required when uploading new file
        if (!this.editForm.value.releaseName || this.editForm.value.releaseName.trim() === '') {
            this.editForm.get('releaseName')?.markAsTouched();
            this.toastrService.error('Release Name is required when uploading a new file');
            return;
        }

        // Attach metadata
        if (this.editForm.value.modelFileId) {
            formData.append('ModelFileId', String(this.editForm.value.modelFileId));
        }
        
        // Use fileName from upload
        const filePathToSave = this.editForm.value.fileName || '';
        formData.append('FilePath', filePathToSave);
        formData.append('ReleaseName', this.editForm.value.releaseName);
        formData.append('Comment', this.data.comment ?? '');
        formData.append('ReleaseDate', this.data.releaseDate ?? '');
        formData.append('UploadedBy', this.data.modifUser ?? '');
        formData.append('ControllerSoftwareVersionID', String(this.data.softwareReleaseId));

        // Prepare release changes
        let modifiedSoftwareReleases: EMTControllerSoftwareReleaseItem[] = [];
        let newSoftwareReleases: EMTControllerSoftwareReleaseItem[] = [];

        let releasesChanges = { 'modifiedReleases': modifiedSoftwareReleases, 'newReleases': newSoftwareReleases };
        formData.append('releasesChanges', JSON.stringify(releasesChanges));

        // Upload using the original model file endpoint
        Utils.httpUploadControllerSoftwareModelFile(
            this.http,
            this.baseUrl,
            formData,
            String(this.data.familyCodeId),
            this,
            (tthis: ModelFileDialogComponent, result: any) => {
                if (result && !result.error) {
                    tthis.dialogRef.close({
                        success: true,
                        errorMssg: null,
                        modelFileId: result.modelFileId || tthis.editForm.value.modelFileId,
                        fileName: filePathToSave,
                        releaseName: tthis.editForm.value.releaseName
                    });
                } else {
                    tthis.dialogRef.close({
                        success: false,
                        errorMssg: result.errorMssg
                    });
                }
            }
        );
    }

    onModelFileFilter(event: Event) {
        const value = (event.target as HTMLInputElement).value;
        this.filterModelFiles(value.toLowerCase());
    }

    filterModelFiles(value: string) {
        // If value is empty, user cleared the input
        if (!value) {
            this.selectedModelFileId = -1;
            this.filteredModelFiles = this.availableModelFiles;
            return;
        }

        this.filteredModelFiles = this.availableModelFiles.filter(f =>
            f.releaseName.toLowerCase().includes(value.toLowerCase())
        );

        // Check if value matches any file path
        const found = this.filteredModelFiles.some(
            file => file.releaseName.toLowerCase() === value.toLowerCase()
        );
        if (!found) {
            this.selectedModelFileId = -1;
        }
    }

    onModelFileSelected(event: MatAutocompleteSelectedEvent) {
        const selectedId = event.option.value;
        const selectedModelFile = this.availableModelFiles.find(f => f.modelFileId === selectedId);
        
        if (selectedModelFile) {
            this.selectedModelFileId = selectedId;
            this.selectedModelFilePath = selectedModelFile.releaseName;
            this.editForm.patchValue({ 
                modelFileId: selectedId 
            });
            
            // Clear uploaded file and release name when selecting from dropdown
            this.editForm.get('file')?.setValue(null);
            this.editForm.get('fileName')?.setValue('');
            this.editForm.get('releaseName')?.setValue('');
            this.isFileChanged = false;

            // Reset validation for 'Release name' since it's not required when selecting existing file
            this.editForm.get('releaseName')?.markAsUntouched();
            this.editForm.get('releaseName')?.markAsPristine();
        }
    }

    onModelFileFormFieldClick() {
        // Load model files only once on first click
        if (!this.modelFilesLoaded) {
            this.emtService.getAvailableModelFiles()
                .subscribe({
                    next: (files: EMTModelFile[]) => {
                        this.availableModelFiles = files;
                        this.filteredModelFiles = files;
                        this.modelFilesLoaded = true;
                    },
                    error: (error) => {
                        console.error('Error loading model files:', error);
                        this.toastrService.error('Failed to load available model files');
                    }
                });
        }
        
        this.selectedModelFileId = -1;
        this.filteredModelFiles = this.availableModelFiles; // Reset filter
    }

    getModelFileText(modelFileId: number): string {
        const file = this.availableModelFiles.find(f => f.modelFileId === modelFileId);
        return file ? file.releaseName : '';
    }

    deleteModelFile() {
        // Call endpoint to remove link in database immediately
        Utils.httpUpdateControllerSoftwareModelFile(
            this.http,
            this.baseUrl,
            this.data.softwareReleaseId,
            0, // Pass 0 to remove the link
            this,
            (tthis: ModelFileDialogComponent, result: any) => {
                if (result && !result.error) {
                    // Clear locally
                    tthis.editForm.patchValue({
                        file: null,
                        modelFileId: 0,
                        fileName: '',
                        releaseName: ''
                    });
                    tthis.selectedModelFileId = -1;
                    tthis.selectedModelFilePath = '';
                    tthis.fileContent = '';
                    tthis.isFileChanged = false;
                    
                    tthis.toastrService.success('Model file unlinked successfully!');
                    
                    // Close the dialog and return success with cleared data
                    tthis.dialogRef.close({
                        success: true,
                        errorMssg: null,
                        modelFileId: 0,
                        fileName: ''
                    });
                } else {
                    tthis.toastrService.error(result?.errorMssg || 'Failed to unlink model file');
                }
            }
        );
    }

    downloadModelFile() {
        const modelFileId = this.editForm.value.modelFileId;
        if (modelFileId) {
            Utils.httpDownloadControllerSoftwareModelFile(
                this.http,
                this.baseUrl,
                modelFileId,
                this,
                (tthis: ModelFileDialogComponent, result: any) => {
                    if (result && !result.error) {
                        // Download the file
                        const blob = new Blob([result], { type: 'application/json' });
                        const url = window.URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = tthis.editForm.value.fileName || 'downloaded-model.json';
                        a.click();
                        window.URL.revokeObjectURL(url);
                        tthis.toastrService.success('Model file downloaded successfully!');
                    } else {
                        tthis.toastrService.error(result?.errorMssg || 'Model file download failed.');
                    }
                }
            );
        }
    }

    onCancel(): void {
        this.dialogRef.close({
            canceled: true,
            changedByUser: this.editForm.value.changedByUser
        });
    }

    isNewFileActive(): boolean {
        // Returns true if user is working with a new file (upload or release name filled)
        return !!(this.editForm.value.file || this.editForm.value.fileName || (this.editForm.value.releaseName && this.editForm.value.releaseName.trim() !== ''));
    }

    get hasReleaseNameChanged(): boolean {
        if (this.data.isNew) return true; // en modo new siempre habilitado
        return this.editForm.value.releaseName?.trim() !== this.originalFormValue.releaseName?.trim()
            || this.editForm.value.comment?.trim() !== this.originalFormValue.comment?.trim();
    }
}

